import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { InferType } from 'yup';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Fade,
  Alert,
  Collapse
} from '@mui/material';
import { 
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Shield as ShieldIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { 
  login, 
  selectAuth, 
  clearAuthError,
  resetTwoFactorState
} from '../store/slices/authSlice';
import { LoginRequest } from '../types/auth.types';
import { AppDispatch } from '../store';

// Схема валидации для формы входа
const schema: yup.ObjectSchema<LoginRequest> = yup.object({
  username: yup.string().required('Имя пользователя обязательно'),
  password: yup.string().required('Пароль обязателен'),
  twoFactorCode: yup.string().optional().when('$requiresTwoFactor', {
    is: true,
    then: (schema) => schema.required('Код 2FA обязателен'),
  }),
});

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading, error, requiresTwoFactor } = useSelector(selectAuth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Использование react-hook-form для управления формой
  type LoginFormType = InferType<typeof schema>;

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<LoginFormType>({
    resolver: yupResolver(schema),
    context: { requiresTwoFactor },
    defaultValues: { username: '', password: '', twoFactorCode: '' },
  });

  // Анимация появления
  useEffect(() => {
    setMounted(true);
  }, []);

  // Наблюдаем за флагом двухфакторной аутентификации
  useEffect(() => {
    if (requiresTwoFactor) {
    }
  }, [requiresTwoFactor, setValue]);

  // Перенаправление при успешной авторизации
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Очистка сообщения об ошибке при размонтировании
  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
      dispatch(resetTwoFactorState());
    };
  }, [dispatch]);

  // Обработчик отправки формы
  const onSubmit = (data: LoginRequest) => {
    dispatch(login(data));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.1)' },
        },
      }}
    >
      <Container maxWidth="sm">
        <Fade in={mounted} timeout={800}>
          <Paper 
            elevation={24}
            sx={{ 
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              }
            }}
          >
            {/* Заголовок */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                  transform: mounted ? 'scale(1)' : 'scale(0)',
                  transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                Добро пожаловать
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Войдите в свой аккаунт для продолжения
              </Typography>
            </Box>

            {/* Сообщение об ошибке */}
            <Collapse in={!!error}>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  animation: 'shake 0.5s',
                  '@keyframes shake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '25%': { transform: 'translateX(-10px)' },
                    '75%': { transform: 'translateX(10px)' },
                  }
                }}
              >
                {error}
              </Alert>
            </Collapse>

            {/* Уведомление о 2FA */}
            <Collapse in={requiresTwoFactor}>
              <Alert 
                severity="info" 
                icon={<ShieldIcon />}
                sx={{ mb: 3, borderRadius: 2 }}
              >
                Введите код двухфакторной аутентификации из вашего приложения
              </Alert>
            </Collapse>
            
            {/* Форма */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Имя пользователя"
                autoComplete="username"
                autoFocus
                {...register('username')}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={isLoading || requiresTwoFactor}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                    }
                  }
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Пароль"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading || requiresTwoFactor}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="переключить видимость пароля"
                        onClick={handleClickShowPassword}
                        edge="end"
                        disabled={isLoading || requiresTwoFactor}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                    }
                  }
                }}
              />
              
              {requiresTwoFactor && (
                <Fade in={requiresTwoFactor}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="twoFactorCode"
                    label="Код двухфакторной аутентификации"
                    {...register('twoFactorCode')}
                    error={!!errors.twoFactorCode}
                    helperText={errors.twoFactorCode?.message}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ShieldIcon sx={{ color: 'action.active' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                        }
                      }
                    }}
                  />
                </Fade>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ 
                  mt: 4,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.3s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={26} sx={{ color: 'white' }} />
                ) : (
                  'Войти'
                )}
              </Button>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Link 
                  href="#" 
                  variant="body2" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/forgot-password');
                  }}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#764ba2',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Забыли пароль?
                </Link>
                <Link 
                  href="#" 
                  variant="body2" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/register');
                  }}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#764ba2',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Создать аккаунт
                </Link>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}